import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DataCacheProvider } from "./context/DataCacheContext";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardLayout from "./components/DashboardLayout";
import AdminDashboard from "./pages/AdminDashboard";
import TeamDashboard from "./pages/TeamDashboard";
import TeamMembersPage from "./pages/TeamMembersPage";
import ClientsPage from "./pages/ClientsPage";
import CasesPage from "./pages/CasesPage";
import DosareBCPage from "./pages/DosareBCPage";
import EvidențaBCPage from "./pages/EvidențaBCPage";
import EvidentaDosarePage from "./pages/EvidentaDosarePage";
import TasksPage from "./pages/TasksPage";
import CalendarPage from "./pages/CalendarPage";
import PortalJustPage from "./pages/PortalJustPage";
import DocumentRequestsPage from "./pages/DocumentRequestsPage";
import AutomatedEmailsPage from "./pages/AutomatedEmailsPage";
import AdminNotesPage from "./pages/AdminNotesPage";
import AppointmentsPage from "./pages/AppointmentsPage";
import SettingsPage from "./pages/SettingsPage";
import BillingPage from "./pages/BillingPage";
import RetrocedariPage from "./pages/RetrocedariPage";
import ArchivedCasesPage from "./pages/ArchivedCasesPage";
import "./App.css";

const ProtectedRoute = ({ children, adminOnly = false, allowedPositions = [], excludedPositions = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse font-heading text-2xl text-primary">Se încarcă...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Admin always has access
  if (user.role === "admin") {
    return children;
  }

  // Check if user's position is excluded
  if (excludedPositions.length > 0 && excludedPositions.includes(user.position)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check if route is admin only
  if (adminOnly) {
    // But also check if user's position is in allowedPositions
    if (allowedPositions.length > 0 && allowedPositions.includes(user.position)) {
      return children;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const DashboardRedirect = () => {
  const { user } = useAuth();
  if (user?.role === "admin") {
    return <AdminDashboard />;
  }
  return <TeamDashboard />;
};

function App() {
  return (
    <AuthProvider>
    <DataCacheProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardRedirect />} />
            <Route
              path="team"
              element={
                <ProtectedRoute adminOnly>
                  <TeamMembersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="clients"
              element={
                <ProtectedRoute adminOnly>
                  <ClientsPage />
                </ProtectedRoute>
              }
            />
            <Route path="cases" element={<CasesPage />} />
            <Route path="dosare-bc" element={<DosareBCPage />} />
            <Route path="evidenta-bc" element={<EvidențaBCPage />} />
            <Route path="evidenta-dosare" element={<EvidentaDosarePage />} />
            <Route
              path="dosare-arhivate"
              element={
                <ProtectedRoute adminOnly>
                  <ArchivedCasesPage />
                </ProtectedRoute>
              }
            />
            <Route path="tasks" element={<TasksPage />} />
            <Route 
              path="calendar" 
              element={
                <ProtectedRoute excludedPositions={["colaborator_extern"]}>
                  <CalendarPage />
                </ProtectedRoute>
              } 
            />
            <Route path="portal-just" element={<PortalJustPage />} />
            <Route path="document-requests" element={<DocumentRequestsPage />} />
            <Route
              path="admin-notes"
              element={
                <ProtectedRoute adminOnly>
                  <AdminNotesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="appointments"
              element={
                <ProtectedRoute adminOnly allowedPositions={["avocat_colaborator"]}>
                  <AppointmentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="automated-emails"
              element={
                <ProtectedRoute adminOnly>
                  <AutomatedEmailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings"
              element={
                <ProtectedRoute adminOnly>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="billing"
              element={
                <ProtectedRoute>
                  <BillingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="retrocedari"
              element={
                <ProtectedRoute adminOnly>
                  <RetrocedariPage />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </DataCacheProvider>
    </AuthProvider>
  );
}

export default App;
